import { Avatar } from "../components/Avatar";
import { Card, CardActionButton } from "../components/Card";
import { TruncatedText } from "../components/TruncatedText";
import { DotsHorizontal } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import React from "react";
/**
 * The "more" (dots) action button for an AssistantCard's `action` slot, commonly
 * used as a DropdownMenu trigger for secondary controls (edit, duplicate, remove).
 * @summary Actions button for assistant cards.
 */
export const AssistantCardMore = React.forwardRef(({ ...props }, ref) => {
    return (React.createElement(CardActionButton, { size: "xs", ref: ref, icon: DotsHorizontal, ...props }));
});
AssistantCardMore.displayName = "AssistantCardMore";
/**
 * A card presenting an agent for browsing or selection, showing its title, avatar,
 * subtitle (authors), and description, with an optional `action` slot. Use it in
 * agent galleries, pickers, or lists laid out with CardGrid. For a denser tile use
 * CompactAssistantCard; for a wide two-column list row use LargeAssistantCard.
 * @summary Agent card for galleries and pickers.
 */
export const AssistantCard = React.forwardRef(({ className, onClick, onContextMenu, title, description, descriptionLineClamp = 2, pictureUrl, subtitle, action, variant = "primary", iconSize = "md", }, ref) => {
    return (React.createElement(Card, { ref: ref, size: "sm", className: cn("flex flex-col gap-3", className), onClick: onClick, onContextMenu: onContextMenu, action: action, variant: variant },
        React.createElement("div", { className: "flex gap-2" },
            React.createElement(Avatar, { visual: pictureUrl, size: iconSize }),
            React.createElement("div", { className: cn("-mt-0.5 flex flex-col", action && "pr-8") },
                React.createElement("h3", null,
                    React.createElement(TruncatedText, { lineClamp: 1, className: "text-sm font-medium overflow-hidden text-ellipsis break-all notranslate" }, title)),
                React.createElement("p", { className: cn("line-clamp-1 overflow-hidden text-ellipsis text-xs", "text-faint dark:text-muted-foreground") }, subtitle))),
        description && (React.createElement(TruncatedText, { lineClamp: descriptionLineClamp, className: cn("overflow-hidden text-ellipsis pb-1 text-xs", "text-muted-foreground") }, description))));
});
AssistantCard.displayName = "AssistantCard";
/**
 * The wide variant of AssistantCard for two-column lists: a large avatar next to
 * the title and an up-to-five-line description. Prefer AssistantCard or
 * CompactAssistantCard for grids.
 * @summary Wide agent card for lists.
 */
export const LargeAssistantCard = React.forwardRef(({ className, onClick, title, description, pictureUrl }, ref) => {
    return (React.createElement(Card, { ref: ref, size: "lg", className: className, onClick: onClick, variant: "tertiary" },
        React.createElement("div", { className: "flex gap-3" },
            React.createElement(Avatar, { visual: pictureUrl, size: "lg" }),
            React.createElement("div", { className: cn("flex flex-col gap-2 text-base", "text-foreground") },
                React.createElement("h3", { className: "heading-base" }, title),
                React.createElement("p", { className: cn("line-clamp-5 overflow-hidden text-ellipsis", "text-muted-foreground") }, description)))));
});
LargeAssistantCard.displayName = "LargeAssistantCard";
/**
 * The dense tile variant of AssistantCard for grids: a small avatar stacked above
 * the title and a clamped description. Prefer LargeAssistantCard for wide list rows.
 * @summary Dense agent tile for grids.
 */
export const CompactAssistantCard = React.forwardRef(({ className, onClick, title, description, pictureUrl, variant = "secondary", }, ref) => {
    return (React.createElement(Card, { ref: ref, size: "md", className: cn("cursor-pointer flex flex-col items-start gap-1", className), onClick: onClick, variant: variant },
        React.createElement(Avatar, { visual: pictureUrl, size: "sm" }),
        React.createElement("div", { className: "min-w-0" },
            React.createElement("h3", { className: "heading-base line-clamp-1 text-foreground" }, title),
            React.createElement("p", { className: cn("line-clamp-3 text-sm", "text-muted-foreground") }, description))));
});
CompactAssistantCard.displayName = "CompactAssistantCard";
//# sourceMappingURL=AssistantCard.js.map